import { Authenticator, Heading, useAuthenticator, View } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { signUp, SignUpInput } from "aws-amplify/auth";
import { useMemo } from "react";
import "./customAuthenticator.css";
import { Loader2, CheckCircle2, Shield, Lock, Stethoscope } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ICustomAuthenticatorProps {
    authLoading: boolean;
    showLoginOnly?: boolean;
}

const CustomDoctorAuthenticator = ({ authLoading, showLoginOnly = false }: ICustomAuthenticatorProps) => {
    const isLargeScreen = typeof window !== 'undefined' && window.innerWidth >= 768;

    const services = {};

    const formFields = {
        signUp: {
            name: { placeholder: "Enter your name", label: "Name", order: 1, required: true },
            email: { placeholder: "Enter your email", label: "Email", order: 2, required: true },
            phone_number: { placeholder: "Enter your phone number", label: "Phone Number", order: 3, required: true },
            password: { placeholder: "Enter your password", label: "Password", order: 4, required: true },
            confirm_password: { placeholder: "Confirm your password", label: "Confirm Password", order: 5, required: true },
            "custom:role": { type: "hidden", value: "doctor", order: 6 },
        },
    };

    const authComponent = useMemo(() => (
        <Authenticator
            formFields={showLoginOnly ? {} : formFields}
            initialState={showLoginOnly ? "signIn" : "signUp"}
            loginMechanisms={["email"]}
            components={CustomComponents(showLoginOnly)}
            services={showLoginOnly ? {} : services}
        />
    ), []);

    return (
        <div className="h-screen w-full overflow-hidden">
            <div className="flex h-full w-full">
                {/* ═══ LEFT: Premium Branded Panel ═══ */}
                {isLargeScreen && (
                    <div className="hidden md:flex w-[48%] flex-col relative overflow-hidden min-h-screen bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700"
                    >
                        {/* Decorative elements */}
                        <div className="absolute inset-0 opacity-[0.03] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:28px_28px]"
                        />
                        <div className="absolute -top-32 -right-32 w-96 h-96 bg-brand-400/15 rounded-full blur-[80px]" />
                        <div className="absolute bottom-20 -left-20 w-72 h-72 bg-accent-500/10 rounded-full blur-[60px]" />
                        <div className="absolute top-1/2 right-10 w-40 h-40 bg-brand-300/8 rounded-full blur-[50px]" />

                        {/* Content */}
                        <div className="relative z-10 flex flex-col justify-between h-full px-10 xl:px-14 py-10">
                            {/* Logo */}
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10">
                                    <Stethoscope size={22} className="text-white/90" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-bold text-white tracking-tight">Symbian Health</h1>
                                    <p className="text-nav-xs text-white/40 font-medium tracking-wide">Provider Portal</p>
                                </div>
                            </div>

                            {/* Main Content */}
                            <div className="flex-1 flex flex-col justify-center -mt-8">
                                <h2 className="text-display-sm xl:text-display font-bold text-white leading-[1.2] tracking-tight mb-3">
                                    Provider
                                    <br />
                                    Access Portal
                                </h2>
                                <p className="text-sm text-white/45 leading-relaxed mb-10 max-w-[380px]">
                                    Secure access for healthcare professionals to review cases and provide expert second opinions.
                                </p>

                                {/* Reasons List */}
                                <div className="space-y-3.5">
                                    {PROVIDER_REASONS.map((item, index) => (
                                        <div key={index}
                                            className="flex items-start gap-3 group"
                                        >
                                            <div className="w-6 h-6 rounded-lg bg-accent-500/15 flex items-center justify-center flex-shrink-0 mt-0.5 border border-accent-400/15 group-hover:bg-accent-500/25 transition-colors duration-300">
                                                <CheckCircle2 size={14} className="text-accent-400" />
                                            </div>
                                            <div>
                                                <span className="text-sm font-semibold text-white/85 leading-snug">{item.title}</span>
                                                <p className="text-compact text-white/35 mt-0.5 leading-relaxed">{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Trust Badges */}
                            <div className="border-t border-white/8 pt-6">
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <Shield size={14} className="text-accent-400/70" />
                                        <span className="text-nav-xs text-white/35 font-medium">HIPAA Compliant</span>
                                    </div>
                                    <div className="w-px h-3 bg-white/10" />
                                    <div className="flex items-center gap-2">
                                        <Lock size={14} className="text-accent-400/70" />
                                        <span className="text-nav-xs text-white/35 font-medium">256-bit Encrypted</span>
                                    </div>
                                    <div className="w-px h-3 bg-white/10" />
                                    <span className="text-nav-xs text-white/35 font-medium">GDPR Ready</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ RIGHT: Auth Form ═══ */}
                <div className={`${isLargeScreen ? 'w-[52%]' : 'w-full'} flex flex-col justify-center items-center overflow-y-auto bg-surface-50 relative`}>
                    {/* Mobile logo (shown only on small screens) */}
                    {!isLargeScreen && (
                        <div className="w-full flex items-center justify-center gap-2.5 pt-8 pb-2">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
                                <Stethoscope size={18} className="text-white" />
                            </div>
                            <span className="text-lg font-bold text-navy-500">Symbian Health</span>
                        </div>
                    )}

                    {!authLoading ? (
                        <div className="w-full max-w-md px-6 py-8 auth-custom-container auth-fade-in">
                            {authComponent}
                        </div>
                    ) : (
                        <div className="w-full h-full flex flex-col justify-center items-center gap-4 animate-fade-in">
                            <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center">
                                <Loader2 size={32} className="animate-spin text-brand-500" />
                            </div>
                            <p className="text-sm text-slate-400 font-medium">Preparing your account…</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const CustomComponents = (loginOnly = false) => ({
    SignIn: {
        Header() {
            return (
                <div className="px-8 pt-6 pb-2">
                    <h3 className="text-xl font-bold text-navy-500">Provider Sign In</h3>
                    <p className="text-sm text-slate-400 mt-1">Access your medical review dashboard</p>
                </div>
            );
        },
        Footer() {
            const { toForgotPassword } = useAuthenticator();
            return (
                <View style={{ display: "flex", justifyContent: "center", width: "100%", padding: "8px 24px 16px" }}>
                    <CustomLink onClick={toForgotPassword}>Forgot Password?</CustomLink>
                    {!loginOnly && (
                        <CustomLink onClick={() => { }}>Don't have an account?</CustomLink>
                    )}
                </View>
            );
        }
    },
    ...(loginOnly
        ? {}
        : {
            SignUp: {
                Header() {
                    return (
                        <div className="px-8 pt-6 pb-2">
                            <h3 className="text-xl font-bold text-navy-500">Provider Registration</h3>
                            <p className="text-sm text-slate-400 mt-1">Join our network of medical specialists</p>
                        </div>
                    );
                },
                Footer() {
                    const { toSignIn } = useAuthenticator();
                    const navigate = useNavigate();
                    return (
                        <View style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 24px 16px" }}>
                            <CustomLink onClick={toSignIn}>Already have an account? <span className="font-bold text-brand-600">Sign In</span></CustomLink>
                            <CustomLink onClick={() => navigate('/')}>Are You a Patient?</CustomLink>
                        </View>
                    );
                }
            }
        })
});

const CustomLink = ({ onClick, children }: { onClick: () => void, children: React.ReactNode }) => {
    return (
        <button
            onClick={onClick}
            className="text-slate-500 hover:text-brand-500 hover:bg-brand-50 px-2.5 py-1 rounded-lg text-sm transition-all duration-200"
        >
            {children}
        </button>
    );
};

const PROVIDER_REASONS = [
    { title: "Review Patient Cases", desc: "Access comprehensive case files securely" },
    { title: "Provide Expert Opinions", desc: "Share your specialized medical knowledge" },
    { title: "Secure Communication", desc: "End-to-end encrypted messaging" },
    { title: "Flexible Schedule", desc: "Review cases at your convenience" },
    { title: "Collaborative Platform", desc: "Connect with specialists worldwide" },
    { title: "Impact Patient Lives", desc: "Help patients make informed decisions" },
];

export default CustomDoctorAuthenticator;